import { createStyles } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import { UserRole } from '@repo/common/enums/UserRole';
import { stripMarkdownLinks } from '@repo/common/utils';
import { useMount } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { Helmet } from 'react-helmet';
import { useSuspenseQuestion } from '../../api';
import { useAuthUser } from '../../api/hooks/authUser';
import { useQuestionsEnabled } from '../../api/hooks/workspace';
import { AIAssistantSidebar } from '../../components/AIAssistant/AIAssistantSidebar/AIAssistantSidebar';
import { EmptyState } from '../../components/EmptyState';
import EntityPageActions from '../../components/EntityPageLayout/EntityPageActions';
import EntityPageNavBar from '../../components/EntityPageLayout/EntityPageNavBar';
import EntityPageToggles from '../../components/EntityPageLayout/EntityPageToggles';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import {
	PageLayoutContent,
	PageLayoutContentWrapper,
	PageLayoutOuterWrapper,
	PageLayoutWrapper,
} from '../../components/PageLayout';
import {
	RightSidebarMode,
	rightSidebarStore,
	RightSidebarWrapper,
} from '../../components/RightSidebar';
import { QuestionContentController } from '../../features/QuestionContent';
import { QuestionSidebarController } from '../../features/QuestionSidebar';
import { useParamsIdSuffixUuid } from '../../utils/hook/utils';

const useStyles = createStyles((theme) => ({
	wrapper: {
		marginBottom: 0,
		paddingBottom: theme.other.space[12],
	},
}));

const QuestionPage = observer(() => {
	const id = useParamsIdSuffixUuid();
	const { user, workspace } = useAuthUser();
	const questionsEnabled = useQuestionsEnabled();

	const { classes } = useStyles();

	if (!questionsEnabled) {
		return (
			<EmptyState
				title="Questions are not enabled"
				description="Please contact your administrator to enable Questions"
				illustrationName="upgrade"
				includeGoBack={false}
				stateHeight="50vh"
				size="lg"
			/>
		);
	}

	const { data: question } = useSuspenseQuestion({
		id,
	});

	useMount(() => {
		if (id) {
			trackEvent('question/open', { id }, user, workspace);
		}
	});

	return (
		<PageLayoutOuterWrapper>
			<Helmet>
				<title>
					{stripMarkdownLinks(question.title_cased ?? question.title)}
				</title>
			</Helmet>
			<PageLayoutWrapper name="entity-page" key={question.id}>
				<PageLayoutContentWrapper name="entity-page">
					<EntityPageNavBar
						entity={question}
						actions={<EntityPageActions entity={question} />}
						toggles={<EntityPageToggles entity={question} />}
					/>
					<PageLayoutContent className={classes.wrapper}>
						<FeatureGuard
							v1AllowedRoles={[
								UserRole.ADMIN,
								UserRole.EDITOR,
								UserRole.VIEWER,
								UserRole.GUEST,
							]}
							v2Permission="Questions.Read"
							featureName="Questions"
							isFeatureIncludedInPlan={true}
							isAlwaysIncludedFeature={true}
							description="Foster collaboration and communication around data. Ask questions and share insights to make data-driven decisions"
						>
							<QuestionContentController id={id} />
						</FeatureGuard>
					</PageLayoutContent>
				</PageLayoutContentWrapper>

				<RightSidebarWrapper>
					{rightSidebarStore.mode === RightSidebarMode.INFO && (
						<QuestionSidebarController question={question} />
					)}
					{rightSidebarStore.mode === RightSidebarMode.AI && (
						<AIAssistantSidebar />
					)}
				</RightSidebarWrapper>
			</PageLayoutWrapper>
		</PageLayoutOuterWrapper>
	);
});

export default QuestionPage;
